import { Checkbox } from "@xola/ui-kit";
import {
    CellClickedEvent,
    ColDef,
    ColumnPinnedType,
    GetDataPath,
    GetMainMenuItemsParams,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    IRowNode,
    ModelUpdatedEvent,
    RedrawRowsParams,
    RefreshCellsParams,
    RowClassParams,
    RowDataUpdatedEvent,
    RowGroupOpenedEvent,
} from "ag-grid-community";
import { LicenseManager } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useMemoizedFn } from "ahooks";
import { isEquipmentBased, isEquipmentItem, Loader, PurchaseItemStatus } from "common";
import { isEmpty, isNil, sortBy, uniqueId } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DigitalWaiver, Event, Experience, ExperiencePurchaseItem, GuestStatus, Timeslot } from "types";
import shallow from "zustand/shallow";
import { getRowHeight, redrawModifiedRow, redrawSummaryRow } from "../../DashboardTable/gridUtils";
import { RosterGuestListForPrint } from "../RosterGuestListForPrint";
import { CatalogInfo, GuestRowData, SummaryData } from "../RosterGuestTypes";
import { calculateTotalWeightForItem, combineWeights } from "../weightHelpers";
import { popoverParentId } from "./ActionCell";
import { CustomizeColumnsSection } from "./CustomizeColumns";
import { CustomRosterViewMenu } from "./CustomRosterViewMenu";
import { autoGroupColumnDef, defaultColDef, getColumnDefs, getTooltipText, RosterColumnId } from "./helpers";
import {
    getGuestsByStatus,
    getSummarizedGuestsData,
} from "@/components/PurchaseModification/CheckInPurchaseGuests/CheckInHelper";
import { config } from "@/config";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useResource } from "@/hooks/useResource";
import { getPrimaryGuest } from "@/models/Guest";
import { getCustomerName } from "@/models/Purchase";
import {
    getAddonLineItems,
    getAttendeeLineItemsForRoster,
    getDemographicLineItems,
    getEquipmentRosterLineItems,
    getShortCode,
} from "@/models/PurchaseItem";
import { DemographicLineItem } from "@/stores/createPurchaseStore";
import "./RosterGuestList.css";
import { useQuestionnairePreference, XOLA_DEFAULT_CONFIG_NAME } from "@/hooks/questionnaireFormHooks";
import { useCollection } from "@/hooks/useCollection";
import { hasUniqueGuestIdentification, isCheckInAllowed } from "@/models/Experience";
import { isProductHasLinkedWaiverTemplate } from "@/models/Waiver";
import { useDashboardRosterStore, useModalStore } from "@/stores/dashboardFilterStore";
import { canViewPurchase, canViewWaivers } from "@/stores/globalStore";

interface RosterGuestListProps {
    timeslot: Timeslot;
    experience: Experience;
    isPrintRosterGuestListVisible?: boolean;
    eventBannerRef: React.RefObject<HTMLDivElement>;
}

/** Returns the name of the organizer to display in roster */
export const getOrganizerName = (item: ExperiencePurchaseItem) => {
    const customerName = getCustomerName(item.purchase);
    const defaultName = getShortCode(item)?.toUpperCase() ?? "Organizer";
    return isEmpty(customerName) ? defaultName : customerName;
};

/**
 * Rolls this item's add-ons into its own tally and the roster-wide one in a single pass.
 */
const collectAddOns = (item: ExperiencePurchaseItem, summaryAddOns: CatalogInfo): CatalogInfo => {
    const addOns: CatalogInfo = { value: {}, length: 0 };

    for (const addon of getAddonLineItems(item)) {
        if (addon.name && addon.quantity > 0) {
            summaryAddOns.value[addon.name] = (summaryAddOns.value[addon.name] ?? 0) + (addon.quantity ?? 0);
            addOns.value[addon.name] = (addOns.value[addon.name] ?? 0) + (addon.quantity ?? 0);
        }
    }

    summaryAddOns.length = Object.keys(summaryAddOns.value).length;
    addOns.length = Object.keys(addOns.value).length;

    return addOns;
};

export const RosterGuestList = ({
    timeslot,
    experience,
    isPrintRosterGuestListVisible,
    eventBannerRef,
}: RosterGuestListProps) => {
    const seller = useSeller();

    const gridRef = useRef<AgGridReact>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const parentHeight = containerRef ? containerRef.current?.offsetParent?.clientHeight ?? 0 : 0;
    const selectedView = useDashboardRosterStore((state) => state.selectedRosterView ?? XOLA_DEFAULT_CONFIG_NAME);

    const [columnApi, setColumnApi] = useState<GridApi>();

    const [isModalOpen, modalEventId] = useModalStore((state) => [state.isOpen, state.eventId]);
    const isAttendeeUnique = hasUniqueGuestIdentification(experience);

    const { data: event } = useResource<Event>(["events", timeslot.event.id]);

    const { viewConfigurations } = useQuestionnairePreference([]);

    const { data: waiverTemplates, isLoading: isWaiverTemplatesLoading } = useCollection<DigitalWaiver>(
        "waiverTemplates",
        null,
        { enabled: canViewWaivers() },
    );

    const {
        data: purchaseItems,
        isLoading,
        hasNextPage,
        isFetchingNextPage,
    } = useAutoFetchCollection<ExperiencePurchaseItem>(
        "purchaseItems",
        { event: timeslot.event?.id, expand: "waiver,membershipPurchaseItem,purchaseRuleLink,purchaseRule" },
        { enabled: !!timeslot.event?.id, staleTime: 60 * 1000 },
    );

    useEffect(() => {
        useDashboardRosterStore.setState({ selectedRosterView: null });
    }, [timeslot.id]);

    const { data: columnDefs } = useMemo(
        () =>
            !isWaiverTemplatesLoading && !!selectedView ? getColumnDefs(viewConfigurations, experience) : { data: [] },
        [viewConfigurations, experience, isWaiverTemplatesLoading, selectedView],
    );

    const isLoadingPurchaseItem = hasNextPage ?? isLoading ?? isFetchingNextPage;

    // Ref to store the previous value of filteredItems, This will prevent from creating new filteredItems object even when there is no change in purchaseItems
    const filteredItemsRef = useRef<ExperiencePurchaseItem[]>();

    const filteredItems = useMemo(() => {
        const includedStatuses = new Set([
            PurchaseItemStatus.CONFIRMED,
            PurchaseItemStatus.PENDING_ACCEPT,
            PurchaseItemStatus.PENDING_MINIMUM,
        ]);
        const items = purchaseItems.filter((item) => includedStatuses.has(item.status));

        // Perform shallow comparison on purchase items, Refer X2-11667 for more details.
        if ((isModalOpen && modalEventId === timeslot.event.id) || areItemsEqual(items, filteredItemsRef.current)) {
            // If equal, return the previous filteredItems
            return filteredItemsRef.current;
        }

        // If not equal, update the ref and return the new filteredItems
        filteredItemsRef.current = items;
        return items;
    }, [purchaseItems, isModalOpen, modalEventId, timeslot]);

    const isEquipmentRoster = experience ? isEquipmentBased(experience) : false;
    const [showAttendeeView, showEquipmentView, setRosterSubViews] = useDashboardRosterStore(
        (state) => [state.showAttendeeView, state.showEquipmentView, state.setRosterSubViews],
        shallow,
    );
    const hasAttendees = (filteredItems ?? []).some((item) => (item.guests?.length ?? 0) > 0);

    const handleToggleAttendeeView = useMemoizedFn((e: { target: { checked: boolean } }) => {
        setRosterSubViews({ showAttendeeView: e.target.checked, showEquipmentView });
    });
    const handleToggleEquipmentView = useMemoizedFn((e: { target: { checked: boolean } }) => {
        setRosterSubViews({ showAttendeeView, showEquipmentView: e.target.checked });
    });

    const rowData: Array<GuestRowData | SummaryData> = useMemo(() => {
        if (isLoadingPurchaseItem) {
            return [];
        }

        const sellerCustomerNotes = seller.customerNotes;
        const isWaiverLinked = experience ? isProductHasLinkedWaiverTemplate(waiverTemplates, experience) : false;
        const isCheckinEnabled = experience ? isCheckInAllowed(seller, experience) : false;
        const isEquipmentCheckinEnabled = experience?.allowCheckoutAtArrival ?? false;
        const unitIdsByTemplateId = getUnitIdsByTemplateId(experience);

        const summary: SummaryData = {
            id: `summary-${timeslot.id}`,
            path: ["summary"],
            isAttendeeUnique,
            balance: 0,
            grossPurchaseAmount: 0,
            grossReservationAmount: 0,
            waiverQuantity: 0,
            waivers: [],
            demographics: { value: {}, length: 0 },
            equipments: { value: {}, length: 0 },
            addOns: { value: {}, length: 0 },
            customerNotesCount: 0,
            purchasesNoteCount: 0,
            currency: seller.currency ?? "USD",
            checkInStatus: {
                arrived: 0,
                pending: 0,
                noshow: 0,
                returned: 0,
            },
            groupedGuestsData: filteredItems ? getSummarizedGuestsData(filteredItems) : {},
        };

        /** This is to make organizer sorted in print roster as well */
        const sortedItems = sortBy(filteredItems, (item) => getOrganizerName(item).toUpperCase());
        let weightInKgs = 0;
        let weightInLbs = 0;
        const itemData = sortedItems.flatMap((item) => {
            const [firstGuest] = item.guests; // Set First guest as the organizer, if there is no primary Guest
            const primaryGuest = getPrimaryGuest(item.guests) ?? firstGuest;
            const purchaseNoteCount = item.purchase?.notes?.length;

            summary.purchasesNoteCount += purchaseNoteCount;
            summary.balance += item.purchase.balance;
            summary.grossPurchaseAmount += item.purchase.amount;
            summary.grossReservationAmount += item.amount;
            summary.waiverQuantity += isWaiverLinked ? item.quantity : 0;
            summary.waivers = [...summary.waivers, ...(item.waivers ?? [])];
            summary.currency = item.purchase.currency;
            const addOns = collectAddOns(item, summary.addOns);

            const demographicLineItems = isEquipmentItem(item)
                ? getAttendeeLineItemsForRoster(item)
                : getDemographicLineItems(item);
            const equipmentLineItems = isEquipmentItem(item) ? getEquipmentRosterLineItems(item) : [];
            const demographicLineItemsByGuestKey = getDemographicLineItemsByGuestKey(
                demographicLineItems,
                unitIdsByTemplateId,
            );

            for (const demographic of demographicLineItems) {
                if (demographic.quantity > 0) {
                    if (summary.demographics.value[demographic.name]) {
                        summary.demographics.value[demographic.name] += demographic.quantity;
                    } else {
                        summary.demographics.value[demographic.name] = demographic.quantity;
                    }
                }
            }

            summary.demographics.length = Object.keys(summary.demographics.value).length;

            if (isCheckinEnabled) {
                const guestStatusMap: { [key in GuestStatus]: number } = getGuestsByStatus(item);
                for (const key of Object.keys(guestStatusMap)) {
                    summary.checkInStatus[key as GuestStatus] += guestStatusMap[key as GuestStatus];
                }
            }

            const organizerId = item.purchase.organizer?.id ?? uniqueId("organizer");
            const organizerName = getOrganizerName(item);
            const itemDemographicsFormatted: { [key: string]: number } = {};

            for (const demographic of demographicLineItems) {
                if (demographic.quantity > 0) {
                    itemDemographicsFormatted[demographic.name] = demographic.quantity;
                }
            }

            // Equipment column: equipment line item names/quantities
            const itemEquipmentFormatted: { [key: string]: number } = {};
            for (const equipment of equipmentLineItems) {
                if (equipment.quantity > 0) {
                    itemEquipmentFormatted[equipment.name] = equipment.quantity;
                    summary.equipments.value[equipment.name] =
                        (summary.equipments.value[equipment.name] ?? 0) + equipment.quantity;
                }
            }
            summary.equipments.length = Object.keys(summary.equipments.value).length;

            const { weightInKgs: itemWeightInKgs, weightInLbs: itemWeightInLbs } = calculateTotalWeightForItem(item);
            weightInKgs += itemWeightInKgs;
            weightInLbs += itemWeightInLbs;

            const organizer: GuestRowData = {
                path: [item.id],
                organizer: { id: organizerId, name: organizerName },
                isOrganizer: true,
                purchaseNotes: item.purchase.notes,
                isAttendeeUnique,
                item,
                itemDemographics: {
                    value: itemDemographicsFormatted,
                    length: Object.keys(itemDemographicsFormatted).length,
                },
                itemEquipments: {
                    value: itemEquipmentFormatted,
                    length: Object.keys(itemEquipmentFormatted).length,
                },
                parentRef: containerRef,
                isCheckInAllowed: isCheckinEnabled,
                isWaiverLinked,
                addOns,
                weight: combineWeights(itemWeightInKgs, itemWeightInLbs),
            };

            // Equipment items nest guest/equipment rows under "Attendees"/"Equipment" sub-summary rows,
            // gated by the view toggles. Non-equipment items keep guest rows directly under the organizer.
            const isEquipment = isEquipmentItem(item);
            const guestParentPath = isEquipment ? [item.id, "attendees"] : [item.id];

            const data = item.guests.map((guest) => {
                const isPrimaryGuest = guest === primaryGuest;
                const path = [...guestParentPath, guest.id];
                const travelerId = guest.traveler?.id ?? (isPrimaryGuest ? item.purchase.organizer?.id : undefined);
                const guestNotes =
                    travelerId && sellerCustomerNotes ? Object.values(sellerCustomerNotes[travelerId] ?? []) : [];

                const demographic =
                    demographicLineItemsByGuestKey[guest.unit?.id ?? ""] ??
                    demographicLineItemsByGuestKey[guest.template?.id ?? ""];

                if (guestNotes.length > 0) {
                    summary.customerNotesCount++;
                }

                return {
                    path,
                    guest,
                    travelerId,
                    demographic,
                    guestNotes,
                    item,
                    isPrimaryGuest,
                    parentRef: containerRef,
                    isCheckInAllowed: isCheckinEnabled,
                    isWaiverLinked,
                };
            });

            if (!isEquipment) {
                return isAttendeeUnique ? [organizer, ...data] : [organizer];
            }

            return [
                organizer,
                ...buildEquipmentSubRows({
                    item,
                    guestRows: data,
                    itemDemographicsFormatted,
                    itemEquipmentFormatted,
                    showAttendeeView,
                    showEquipmentView,
                    isEquipmentCheckinEnabled,
                    parentRef: containerRef,
                }),
            ];
        });

        summary.weight = combineWeights(weightInKgs, weightInLbs);

        return [summary, ...itemData];
    }, [
        seller,
        experience,
        filteredItems,
        isAttendeeUnique,
        isLoadingPurchaseItem,
        waiverTemplates,
        timeslot?.id,
        showAttendeeView,
        showEquipmentView,
    ]);

    const onGridReady = (params: GridReadyEvent) => {
        setColumnApi(params.api);
    };

    const onColumnPinned = (maxPinnedColumns: number) => {
        if (columnApi) {
            const pinnedColumns = columnApi.getAllGridColumns().filter((col: any) => col.isPinned());
            const allColumns = columnApi.getAllDisplayedColumns();

            if (pinnedColumns.length >= maxPinnedColumns) {
                for (const col of allColumns) {
                    if (!col.isPinned()) {
                        const colDef = col.getColDef();
                        colDef.lockPinned = true;
                    }
                }
            } else {
                for (const col of allColumns) {
                    const colDef = col.getColDef();
                    colDef.lockPinned = false;
                }
            }
        }
    };

    const PinSubMenuItems = [
        {
            name: "Pin Left",
            action: (params: GetMainMenuItemsParams) => handlePinAction(params, "left"),
        },
        {
            name: "Pin Right",
            action: (params: GetMainMenuItemsParams) => handlePinAction(params, "right"),
        },
        {
            name: "No Pin",
            action: (params: GetMainMenuItemsParams) => handlePinAction(params, null),
        },
    ];

    const handlePinAction = (params: GetMainMenuItemsParams, position: ColumnPinnedType) => {
        const col = params.column;
        if (!col || !columnApi) return;

        columnApi.applyColumnState({
            state: [{ colId: col.getColId(), pinned: position }],
            applyOrder: true,
        });
    };

    const getMainMenuItems = (params: GetMainMenuItemsParams) => {
        return PinSubMenuItems.map((item) => {
            return {
                name: item.name,
                action: () => item.action(params),
            };
        });
    };

    const tableRef = useRef<{ height: number | null }>({ height: null });

    // This sets the height value in pixels for 100% of the container's height on the first render
    useEffect(() => {
        if (containerRef.current?.offsetHeight && isNil(tableRef.current.height)) {
            tableRef.current.height = containerRef.current.offsetHeight;
        }
    }, [containerRef]);

    const onModelUpdated = (params: ModelUpdatedEvent) => {
        redrawModifiedRow(params.api);
        setTableHeight();
    };

    const setTableHeight = useCallback(() => {
        if (gridRef.current && containerRef.current) {
            const { api } = gridRef.current;
            const rowNodes: IRowNode[] = [];

            // Get visible rows
            api.forEachNode((node) => {
                if (node.rowHeight && node.displayed) {
                    rowNodes.push(node);
                }
            });

            const summaryRowsHeight = rowNodes.reduce((accumulator, currentValue) => {
                return accumulator + currentValue.rowHeight!;
            }, 0);

            // 52px is height of Table Header
            const rowsHeight = summaryRowsHeight + 52;

            // Check if we need to make the table smaller than 100% container height,
            // for cases with few records (less or equal to full container height).

            // Total height needed for the number of records, when there are less than required to cover all available space.

            // Always a pixel height. Switching between "100%" and pixels resizes the grid viewport in one
            // step (e.g. 501px -> 722px), which re-anchors the scroll and jumps the view to another booking.
            // Capping at parentHeight keeps the same "shrink to fit few rows" behaviour without the unit flip.
            // parentHeight is 0 until the ref attaches (and whenever an ancestor is hidden), so fall back to
            // filling the parent rather than collapsing the grid to 0px.
            const height = parentHeight > 0 ? `${Math.min(rowsHeight, parentHeight)}px` : "100%";

            if (containerRef.current.style.height !== height) {
                containerRef.current.style.height = height;
            }
        }
    }, [parentHeight]);

    if (isLoadingPurchaseItem) {
        return <Loader />;
    }

    const printColumnDefs = (gridRef.current?.api?.getColumnDefs() as ColDef[]) ?? [];
    const eventBannerScrollWidth = eventBannerRef.current?.scrollWidth ?? null;

    return (
        // The height of the container is 100% by default we will adjust it on 1st render and data change.
        <div ref={containerRef} className="h-full">
            <div className="flex justify-between">
                <div className="mb-6 flex items-center space-x-4">
                    <h3>Guests</h3>
                    {isEquipmentRoster && (
                        <div className="flex items-center space-x-4">
                            <Checkbox
                                name="attendee-view"
                                checked={hasAttendees && showAttendeeView}
                                disabled={!hasAttendees}
                                label="Attendee View"
                                onChange={handleToggleAttendeeView}
                            />
                            <Checkbox
                                name="equipment-view"
                                checked={showEquipmentView}
                                label="Equipment View"
                                onChange={handleToggleEquipmentView}
                            />
                        </div>
                    )}
                </div>

                <div className="flex space-x-2">
                    <CustomRosterViewMenu gridRef={gridRef} experience={experience} timeslot={timeslot} />
                    <CustomizeColumnsSection gridRef={gridRef} experience={experience} />
                </div>
            </div>
            <div
                className="ag-theme-alpine ui-roster-guest-list h-full"
                id={popoverParentId}
                style={eventBannerScrollWidth ? { width: `${eventBannerScrollWidth}px` } : {}}
            >
                <AgGridReact
                    ref={gridRef}
                    suppressCellFocus
                    suppressContextMenu
                    suppressScrollOnNewData
                    suppressRowHoverHighlight
                    enableCellTextSelection
                    treeData
                    tooltipMouseTrack
                    theme="legacy"
                    rowData={rowData}
                    rowBuffer={30}
                    rowClassRules={rowClassRules}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    autoGroupColumnDef={autoGroupColumnDef}
                    groupDefaultExpanded={0}
                    tooltipShowDelay={0}
                    getDataPath={getDataPath}
                    context={{ event }}
                    getRowId={getRowId}
                    getMainMenuItems={getMainMenuItems}
                    getRowHeight={getRowHeight}
                    onGridReady={onGridReady}
                    onColumnPinned={() => onColumnPinned(3)}
                    onCellClicked={cellClickedHandler}
                    onRowGroupOpened={refreshCell}
                    onModelUpdated={onModelUpdated}
                    onRowDataUpdated={onRowDataUpdated}
                />
            </div>

            {/* The slice removes the first header with action buttons - not required for printing */}
            {!isLoading && columnApi && isPrintRosterGuestListVisible && printColumnDefs.length > 0 && (
                <RosterGuestListForPrint
                    rowData={rowData}
                    columnDefs={printColumnDefs}
                    timeslot={timeslot}
                    event={event}
                />
            )}
        </div>
    );
};

const buildEquipmentSubRows = ({
    item,
    guestRows,
    itemDemographicsFormatted,
    itemEquipmentFormatted,
    showAttendeeView,
    showEquipmentView,
    isEquipmentCheckinEnabled,
    parentRef,
}: {
    item: ExperiencePurchaseItem;
    guestRows: GuestRowData[];
    itemDemographicsFormatted: { [key: string]: number };
    itemEquipmentFormatted: { [key: string]: number };
    showAttendeeView: boolean;
    showEquipmentView: boolean;
    isEquipmentCheckinEnabled: boolean;
    parentRef: React.RefObject<HTMLDivElement>;
}): GuestRowData[] => {
    const attendeeRows: GuestRowData[] =
        showAttendeeView && item.guests.length > 0
            ? [
                  {
                      path: [item.id, "attendees"],
                      rosterSection: "attendees",
                      item,
                      parentRef,
                      itemDemographics: {
                          value: itemDemographicsFormatted,
                          length: Object.keys(itemDemographicsFormatted).length,
                      },
                  },
                  ...guestRows,
              ]
            : [];

    const equipmentUnitRows: GuestRowData[] = (item.equipments ?? []).map((equipment) => ({
        path: [item.id, "equipment", equipment.id],
        equipment,
        item,
        parentRef,
        isCheckInAllowed: isEquipmentCheckinEnabled,
    }));

    const equipmentRows: GuestRowData[] = showEquipmentView
        ? [
              {
                  path: [item.id, "equipment"],
                  rosterSection: "equipment",
                  item,
                  parentRef,
                  itemEquipments: {
                      value: itemEquipmentFormatted,
                      length: Object.keys(itemEquipmentFormatted).length,
                  },
              },
              ...equipmentUnitRows,
          ]
        : [];

    return [...attendeeRows, ...equipmentRows];
};

/** Maps each catalog item's line item template to the unit it belongs to. */
const getUnitIdsByTemplateId = (experience?: Experience) => {
    const unitIdsByTemplateId: Record<ID, ID> = {};

    for (const catalogItem of experience?.catalog?.items ?? []) {
        if (catalogItem.template?.id && catalogItem.unit?.id) {
            unitIdsByTemplateId[catalogItem.template.id] = catalogItem.unit.id;
        }
    }

    return unitIdsByTemplateId;
};

const getDemographicLineItemsByGuestKey = (lineItems: DemographicLineItem[], unitIdsByTemplateId: Record<ID, ID>) => {
    const lineItemsByKey: Record<ID, DemographicLineItem> = {};

    for (const lineItem of lineItems) {
        const unitId = unitIdsByTemplateId[lineItem.template.id];

        if (unitId) {
            lineItemsByKey[unitId] = lineItem;
        }

        lineItemsByKey[lineItem.template.id] = lineItem;
    }

    return lineItemsByKey;
};

export const getRowId = (params: GetRowIdParams) => {
    const { data } = params;

    if (data?.id) {
        if (data.id?.startsWith("summary") && isSummaryExpanded(params)) {
            data.expanded = true;
        }
        return data?.id;
    }

    if (data?.item?.id && data?.organizer?.id) {
        return `${data.item.id}-${data.organizer.id}`;
    }

    if (data?.item?.id && data?.rosterSection) {
        return `${data.item.id}-section-${data.rosterSection}`;
    }

    if (data?.item?.id && data?.equipment?.id) {
        return `${data.item.id}-${data.equipment.id}`;
    }

    if (data?.item?.id && data?.guest?.id) {
        return `${data.item.id}-${data.guest.id}`;
    }

    if (data.item?.product?.id && data?.guest?.id) {
        return `${data.item.product.id}-${data.guest.id}`;
    }

    return params?.data?.guest?.id ?? params?.data?.item?.id;
};

const isSummaryExpanded = (params: GetRowIdParams) => {
    const summaryNode = params.api.getDisplayedRowAtIndex(0);
    return summaryNode?.expanded;
};

export const getDataPath: GetDataPath = (data: GuestRowData) => data.path;

// As we are not updating Guest Cell data using rowData on RowGroupOpenedEvent, need to Programmatically refresh Guest
// cell if you need to toggle between Organizer and guest name.
export const refreshCell = (event: RowGroupOpenedEvent) => {
    // Depending on your scroll position the last item's group data may not be visible when clicking on the expand icon.
    // This ensures the index is visible, scrolling the table if needed.
    const rowNodeIndex = event.node.rowIndex;

    // Only on expand: the children counted here are the rows just revealed. On collapse they are already
    // gone, so the target index lands past the last displayed row and the grid scrolls back to an earlier
    // booking instead of staying put.
    if (event.expanded && rowNodeIndex) {
        // factor in child nodes, so we can scroll to correct position
        const childCount = event.node.childrenAfterSort ? event.node.childrenAfterSort.length : 0;
        const newIndex = rowNodeIndex + childCount;
        event.api.ensureIndexVisible(newIndex);
    }

    // Also it will only update Cell (here Guest cell), where it detect changes not the whole row.
    const params: RefreshCellsParams = {
        rowNodes: [event.api.getDisplayedRowAtIndex(event.rowIndex ?? 1)!],
    };
    event.api.refreshCells(params);

    // trigger redraw for the organizer row on open/close in order to change expanded color
    const row = event.api.getDisplayedRowAtIndex(rowNodeIndex ?? 0);
    const redrawRowsParams: RedrawRowsParams | null = row ? { rowNodes: [row] } : null;

    if (redrawRowsParams && event.data.id?.startsWith("summary")) {
        event.api.resetRowHeights();
        event.api.redrawRows(redrawRowsParams);
    }

    if (redrawRowsParams && event.data.isOrganizer) {
        event.api.redrawRows(redrawRowsParams);
    }

    // Equipment "Attendees"/"Equipment" sub-summary rows: redraw so the expand chevron reflects state.
    if (redrawRowsParams && event.data?.rosterSection) {
        event.api.redrawRows(redrawRowsParams);
    }
};

// TODO: Move this code to the base agGrid version later
if (config.agGrid?.key) {
    LicenseManager.setLicenseKey(config.agGrid.key);
}

export const cellClickedHandler = (event: CellClickedEvent) => {
    const copyFields = [
        RosterColumnId.RESERVATION_CODE,
        RosterColumnId.VOUCHERS,
        RosterColumnId.PHONE,
        RosterColumnId.EMAIL,
    ];

    if (typeof event.value === "string" && copyFields.includes(event.colDef.colId as RosterColumnId)) {
        navigator.clipboard
            .writeText(event.value)
            .then(() => {
                event.colDef.tooltipValueGetter = getTooltipText("Copied");
                event.api.redrawRows();
                event.colDef.tooltipValueGetter = getTooltipText("Click to copy");
                setTimeout(() => event.api.redrawRows(), 400);
            })
            .catch((error) => {
                console.error("Could not copy text:", error);
            });
    }

    // If canViewPurchase true we open Drawer, if it's false we expand Row
    const showPurchaseDrawer = canViewPurchase();

    const columnsToExclude = [
        RosterColumnId.ACTION,
        RosterColumnId.BALANCE_DUE,
        RosterColumnId.NOTES,
        RosterColumnId.CHECK_IN,
        RosterColumnId.PHONE,
        RosterColumnId.RESERVATION_CODE,
        RosterColumnId.VOUCHERS,
        RosterColumnId.EMAIL,
        RosterColumnId.WAIVERS,
        ...(showPurchaseDrawer ? [RosterColumnId.GUEST] : []),
    ];

    const clickTarget = event.event?.target as HTMLElement | null;
    const isButtonClick = !!clickTarget?.closest("button");

    if (!columnsToExclude.includes(event.colDef.colId! as RosterColumnId) && !isButtonClick) {
        event.node.setExpanded(!event.node.expanded);
    }
};

// Not using isEqual from lodash because it does deep equality check and there can be many purchaseItems
export const areItemsEqual = (array1: ExperiencePurchaseItem[], array2?: ExperiencePurchaseItem[]): boolean => {
    if (array1 === array2) {
        return true;
    }

    if (!array2 || array1.length !== array2.length) {
        return false;
    }

    for (const [index, item1] of array1.entries()) {
        const item2 = array2[index];

        if (item1 === undefined && item2 === undefined) {
            continue;
        }

        if (item1 === undefined || item2 === undefined) {
            return false;
        }

        return array1.length === array2.length && array1.every((item, index) => item === array2[index]);
    }

    return true;
};

export const rowClassRules = {
    "organizer-row": (params: RowClassParams) => {
        return params.data.isOrganizer && params.node.expanded;
    },
    "equipment-booking-row": (params: RowClassParams) => {
        return Boolean(params.data?.isOrganizer) && isEquipmentItem(params.data.item);
    },
    "sub-summary-row": (params: RowClassParams) => {
        return Boolean(params.data?.rosterSection);
    },
    "guest-row": (params: RowClassParams) => {
        return (
            params.node.key !== "summary" &&
            !params.data.isOrganizer &&
            !params.data?.rosterSection &&
            !isEquipmentItem(params.data.item)
        );
    },
    "equipment-child-row": (params: RowClassParams) => {
        return (
            params.node.key !== "summary" &&
            !params.data.isOrganizer &&
            !params.data?.rosterSection &&
            isEquipmentItem(params.data.item)
        );
    },
    "summary-row": (params: RowClassParams) => {
        return params.node.key === "summary";
    },
};

const onRowDataUpdated = (params: RowDataUpdatedEvent) => {
    const summaryNode = params.api.getDisplayedRowAtIndex(0);

    if (summaryNode) {
        summaryNode.setExpanded(true);
        redrawSummaryRow(params.api, summaryNode);
    }
};
